/*
 * Copyright 2026 Sony Corporation
 */

import { InputAdornment, TextField } from '@mui/material';
import { useState } from 'react';

export interface NumberFieldProps {
  label: string;
  value: number;
  unit: string;
  /** Decimal places shown while not being edited. */
  digits?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}

/**
 * A number typed, not dragged: committed on Enter or blur, never per keystroke,
 * so "1." on the way to "1.5" is not written as 1.
 */
export const NumberField = ({ label, value, unit, digits = 2, min, max, disabled, onCommit }: NumberFieldProps) => {
  const [draft, setDraft] = useState<string>();

  const commit = () => {
    if (draft === undefined) {
      return;
    }
    setDraft(undefined);
    const parsed = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(parsed)) {
      return;
    }
    const bounded = Math.min(Math.max(parsed, min ?? -Infinity), max ?? Infinity);
    if (bounded !== value) {
      onCommit(bounded);
    }
  };

  return (
    <TextField
      size="small"
      label={label}
      disabled={disabled}
      value={draft ?? value.toFixed(digits)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          commit();
        } else if (event.key === 'Escape') {
          setDraft(undefined);
        }
      }}
      slotProps={{
        input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> },
        htmlInput: { inputMode: 'decimal', 'aria-label': label },
      }}
    />
  );
};
